import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDate } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import Logo from "@/components/Logo";
import { Loader2, LogOut, Phone, MapPin, Package, Truck, CheckCheck, MessageCircle, Navigation } from "lucide-react";

const TABS = [
  { key: "to_collect", label: "À collecter", icon: Package },
  { key: "to_deliver", label: "À livrer", icon: Truck },
  { key: "done", label: "Terminées", icon: CheckCheck },
];

const waNumber = (n) => (n || "").replace(/[^0-9]/g, "");

function urgency(o) {
  const isDelivery = ["Traité", "Dispatché", "Livré"].includes(o.status);
  let dt = null;
  if (isDelivery && o.delivery_date) dt = new Date(o.delivery_date);
  else if (o.collect_date) dt = new Date(`${o.collect_date}T${o.collect_time || "00:00"}`);
  if (o.status === "Livré") return { key: "done", note: "" };
  if (!dt || isNaN(dt.getTime())) return { key: "none", note: "" };
  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.floor((dt - startToday) / 86400000);
  if (days < 0) return { key: "overdue", note: "En retard" };
  if (days === 0) return { key: "today", note: "Aujourd'hui" };
  if (days <= 2) return { key: "soon", note: `Dans ${days}j` };
  return { key: "upcoming", note: "" };
}

const accentClass = {
  overdue: "border-l-4 border-l-red-500",
  today: "border-l-4 border-l-amber-500",
  soon: "border-l-4 border-l-amber-300",
  upcoming: "border-l-4 border-l-slate-200",
  done: "border-l-4 border-l-emerald-400",
  none: "border-l-4 border-l-slate-200",
};

const noteClass = {
  overdue: "bg-red-50 text-red-700 border border-red-200",
  today: "bg-amber-100 text-amber-800 border border-amber-300",
  soon: "bg-amber-50 text-amber-700 border border-amber-200",
};

export default function CourierHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("to_collect");
  const [data, setData] = useState({ to_collect: [], to_deliver: [], done: [] });
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/courier/orders").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };
  const list = data[tab] || [];

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <header className="sticky top-0 z-20 bg-white border-b px-4 h-14 flex items-center justify-between">
        <Logo size={26} />
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-slate-600">{user.name}</span>
          <button data-testid="courier-logout" onClick={doLogout} className="p-2 text-slate-500"><LogOut className="w-5 h-5" /></button>
        </div>
      </header>

      <div className="p-4">
        <h1 className="text-xl font-bold font-heading">Mes commandes</h1>
        <p className="text-slate-500 text-sm">{fmtDate(new Date().toISOString())}</p>
      </div>

      {loading ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>
      ) : (
        <div className="px-4 space-y-3">
          {list.length === 0 && (
            <div className="text-center py-16 bg-white border rounded-2xl">
              <p className="text-slate-400">Aucune commande dans cet onglet</p>
            </div>
          )}
          {list.map((o) => {
            const u = urgency(o);
            const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent((o.address || "") + " " + (o.commune_name || ""))}`;
            return (
            <div
              key={o.id}
              data-testid={`courier-order-${o.order_number}`}
              className={`bg-white border rounded-2xl overflow-hidden ${accentClass[u.key]}`}
            >
              <button
                onClick={() => navigate(`/livreur/commande/${o.id}`)}
                className="w-full text-left p-4 active:bg-slate-50 transition-colors"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-mono font-bold text-brand-700 text-lg">{o.order_number}</div>
                    <div className="font-semibold mt-0.5">{o.client_name}</div>
                  </div>
                  <StatusBadge status={o.status} />
                </div>
                <div className="mt-2 space-y-1 text-sm text-slate-500">
                  <div className="flex items-center gap-2"><Phone className="w-4 h-4" /> {o.whatsapp}</div>
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4" /> {o.commune_name} · {o.address}</div>
                </div>
                <div className="flex justify-between items-center mt-3 pt-3 border-t">
                  <span className="flex items-center gap-2 text-sm text-slate-500">
                    {o.collect_time || fmtDate(o.collect_date)}
                    {u.note && <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${noteClass[u.key] || ""}`}>{u.note}</span>}
                  </span>
                  <span className="font-bold text-lg">{money(o.total)}</span>
                </div>
              </button>
              <div className="grid grid-cols-3 border-t divide-x text-xs font-medium text-slate-600">
                <a data-testid={`courier-card-call-${o.order_number}`} href={`tel:${o.whatsapp}`} className="flex items-center justify-center gap-1.5 py-3 active:bg-slate-50">
                  <Phone className="w-4 h-4 text-brand-600" /> Appeler
                </a>
                <a data-testid={`courier-card-whatsapp-${o.order_number}`} href={`https://wa.me/${waNumber(o.whatsapp)}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 py-3 active:bg-slate-50">
                  <MessageCircle className="w-4 h-4 text-emerald-600" /> WhatsApp
                </a>
                <a data-testid={`courier-card-maps-${o.order_number}`} href={mapsUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 py-3 active:bg-slate-50">
                  <Navigation className="w-4 h-4 text-red-500" /> Itinéraire
                </a>
              </div>
            </div>
          ); })}
        </div>
      )}

      <nav className="fixed bottom-0 inset-x-0 bg-white border-t flex z-20">
        {TABS.map((t) => {
          const count = (data[t.key] || []).length;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              data-testid={`courier-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex-1 flex flex-col items-center py-2.5 gap-0.5 relative ${tab === t.key ? "text-brand-600" : "text-slate-400"}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[11px] font-medium">{t.label}</span>
              {count > 0 && <span className="absolute top-1 right-1/4 bg-brand-600 text-white text-[10px] rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">{count}</span>}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
